/**
 * Interactive card for a mid-turn ask_operator question — the operator-facing
 * half of the elicitation channel. Renders at the bottom of the message log
 * while the agent's turn blocks on the answer.
 *
 * Semantics follow MCP elicitation: ACCEPT (an actual answer) and DECLINE
 * ("I refuse to choose") are distinct outcomes, and the decline escape hatch
 * is always present — an operator must never feel coerced into an option.
 * Cancel (timeout / stop / supersede) is server-side; the card just vanishes
 * via the question_resolved event when that happens.
 */
import { useEffect, useState } from 'react';
import { CircleHelp } from 'lucide-react';
import { Button } from '@onestone/ui/button';
import { Textarea } from '@onestone/ui/textarea';
import { chatStore, type PendingQuestion } from '@/lib/chatStore';

function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m > 0 ? `${m}:${String(s).padStart(2, '0')}` : `${s}s`;
}

export function QuestionCard({ question }: { question: PendingQuestion }) {
  const [now, setNow] = useState(() => Date.now());
  const [text, setText] = useState('');
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Reset per-question input state when a different question replaces this one.
  useEffect(() => {
    setText('');
    setSelected(new Set());
    setSubmitting(false);
  }, [question.id]);

  const deadline = new Date(question.askedAt).getTime() + question.timeoutMs;
  const remaining = deadline - now;

  const accept = (answer: string): void => {
    if (submitting) return;
    setSubmitting(true);
    void chatStore.answerQuestion('accept', answer);
  };
  const decline = (): void => {
    if (submitting) return;
    setSubmitting(true);
    void chatStore.answerQuestion('decline');
  };
  const toggle = (label: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  return (
    <div
      data-testid="question-card"
      className="rounded-lg border border-primary/40 bg-card px-4 py-3 shadow-[inset_0_1px_0_0_rgb(255_255_255/0.04)]"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <CircleHelp className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {question.header || 'Agent question'}
          </span>
        </div>
        {question.timeoutMs > 0 && Number.isFinite(deadline) ? (
          <span
            className="text-xs tabular-nums text-muted-foreground/80"
            title="Time before the agent proceeds without an answer"
          >
            {formatRemaining(remaining)}
          </span>
        ) : null}
      </div>

      <p className="mt-1.5 whitespace-pre-wrap text-sm text-foreground/90">{question.question}</p>

      <div className="mt-3 flex flex-col gap-2">
        {question.questionKind === 'yes_no' ? (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" disabled={submitting} onClick={() => accept('Yes')}>
              Yes
            </Button>
            <Button size="sm" variant="outline" disabled={submitting} onClick={() => accept('No')}>
              No
            </Button>
          </div>
        ) : question.questionKind === 'choice' ? (
          <div className="flex flex-col items-start gap-1.5">
            {question.options.map((opt) => (
              <Button
                key={opt.label}
                size="sm"
                variant="outline"
                disabled={submitting}
                className="h-auto justify-start whitespace-normal py-1.5 text-left"
                onClick={() => accept(opt.label)}
              >
                <span>
                  {opt.label}
                  {opt.description ? (
                    <span className="block text-xs font-normal text-muted-foreground">{opt.description}</span>
                  ) : null}
                </span>
              </Button>
            ))}
          </div>
        ) : question.questionKind === 'multi' ? (
          <div className="flex flex-col gap-1.5">
            {question.options.map((opt) => (
              <label key={opt.label} className="flex cursor-pointer items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-0.5 accent-primary"
                  checked={selected.has(opt.label)}
                  disabled={submitting}
                  onChange={() => toggle(opt.label)}
                />
                <span>
                  {opt.label}
                  {opt.description ? (
                    <span className="block text-xs text-muted-foreground">{opt.description}</span>
                  ) : null}
                </span>
              </label>
            ))}
            <div>
              <Button
                size="sm"
                disabled={submitting || selected.size === 0}
                onClick={() => accept([...selected].join(', '))}
              >
                Submit
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <Textarea
              value={text}
              disabled={submitting}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && text.trim().length > 0) {
                  e.preventDefault();
                  accept(text.trim());
                }
              }}
              placeholder="Type your answer…"
              rows={2}
              className="text-sm"
            />
            <div>
              <Button size="sm" disabled={submitting || text.trim().length === 0} onClick={() => accept(text.trim())}>
                Answer
              </Button>
            </div>
          </div>
        )}

        {/* Spec-required escape hatch: explicit refusal, distinct from an answer. */}
        <div className="flex justify-end">
          <Button
            size="sm"
            variant="ghost"
            disabled={submitting}
            className="h-7 text-xs text-muted-foreground"
            onClick={decline}
          >
            Decline to answer
          </Button>
        </div>
      </div>
    </div>
  );
}
